// IndexedDB 文件存储工具（按登录用户隔离库名，避免换号后模板缓存串号）
import { getIpeUserLocalOwnerId } from './ipeUserLocalState';
import { ownerScopedIndexedDbName } from './ipeUserIndexedDb';

const DB_NAME = 'funding_file_storage';
const DB_VERSION = 2; // 升级版本以支持时间戳字段
const STORE_NAME = 'files';

let dbInstance = null;
/** @type {string|null} */
let dbInstanceOwner = null;
/** @type {string|null} */
let dbInstanceName = null;

function closeDbInstance() {
  if (dbInstance) {
    try {
      dbInstance.close();
    } catch {
      /* ignore */
    }
  }
  dbInstance = null;
  dbInstanceOwner = null;
  dbInstanceName = null;
}

/** 换号 / 退出时关闭连接 */
export function resetFileStorageForOwnerChange() {
  closeDbInstance();
}

// 打开当前用户作用域数据库
const openDB = () => {
  return new Promise((resolve, reject) => {
    const owner = getIpeUserLocalOwnerId();
    const name = ownerScopedIndexedDbName(DB_NAME);
    if (!owner || !name) {
      reject(new Error('未绑定登录用户，无法访问本地文件存储'));
      return;
    }

    if (dbInstance && dbInstanceOwner === owner && dbInstanceName === name) {
      resolve(dbInstance);
      return;
    }

    closeDbInstance();

    const request = indexedDB.open(name, DB_VERSION);

    request.onerror = () => {
      reject(new Error('打开数据库失败'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstanceOwner = owner;
      dbInstanceName = name;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const oldVersion = event.oldVersion;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // 创建新的对象存储
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      } else if (oldVersion < 2) {
        // 版本升级：为现有数据添加时间戳字段
        const store = event.target.transaction.objectStore(STORE_NAME);
        store.openCursor().onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) {
            const data = cursor.value;
            // 为旧数据添加默认时间戳
            if (!data.lastAccessedAt) {
              data.lastAccessedAt = Date.now();
            }
            if (!data.createdAt) {
              data.createdAt = data.lastAccessedAt;
            }
            cursor.update(data);
            cursor.continue();
          }
        };
      }
    };
  });
};

// 保存文件数据到 IndexedDB
export const saveFileToIndexedDB = async (fileId, fileData, fileName, platform, tableData = null) => {
  try {
    if (!fileId || !fileData || !Array.isArray(fileData)) {
      throw new Error('文件 ID 或数据不能为空');
    }
    
    // 将数组转换为 Uint8Array，然后转换为 Blob
    const uint8Array = new Uint8Array(fileData);
    const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ 
        id: fileId, 
        blob: blob,
        fileName: fileName || 'template.xlsx',
        platform: platform || '',
        fileData: fileData, // 同时保存原始数组，方便读取
        tableData: tableData, // 保存表格数据（用户输入的内容）
        lastAccessedAt: Date.now(), // 记录最后访问时间，用于清理
        createdAt: Date.now(), // 记录创建时间
      });
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = (e) => {
        console.error(`保存文件 ${fileId} 到 IndexedDB 失败:`, e);
        reject(new Error('保存文件到 IndexedDB 失败'));
      };
    });
  } catch (error) {
    console.error(`保存文件 ${fileId} 失败:`, error);
    throw error;
  }
};

// 从 IndexedDB 读取文件数据
export const getFileFromIndexedDB = async (fileId) => {
  try {
    if (!fileId) {
      console.warn('文件 ID 为空');
      return null;
    }
    if (!getIpeUserLocalOwnerId()) {
      return null;
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      // 先读取数据
      const readTransaction = db.transaction([STORE_NAME], 'readonly');
      const readStore = readTransaction.objectStore(STORE_NAME);
      const readRequest = readStore.get(fileId);
      
      readRequest.onsuccess = () => {
        const result = readRequest.result;
        if (result) {
          // 异步更新访问时间（不阻塞读取）
          setTimeout(async () => {
            try {
              const updateDb = await openDB();
              const updateTransaction = updateDb.transaction([STORE_NAME], 'readwrite');
              const updateStore = updateTransaction.objectStore(STORE_NAME);
              const updateRequest = updateStore.get(fileId);
              
              updateRequest.onsuccess = () => {
                const updateResult = updateRequest.result;
                if (updateResult) {
                  updateResult.lastAccessedAt = Date.now();
                  updateStore.put(updateResult);
                }
              };
            } catch (e) {
              // 更新访问时间失败不影响读取
            }
          }, 0);
          
          // 优先返回原始数组（fileData），如果没有则从 Blob 转换
          if (result.fileData && Array.isArray(result.fileData)) {
            resolve({
              fileData: result.fileData,
              fileName: result.fileName || 'template.xlsx',
              platform: result.platform || '',
              tableData: result.tableData || null, // 返回表格数据
            });
          } else if (result.blob) {
            // 如果只有 Blob，需要转换为数组
            const reader = new FileReader();
            reader.onload = () => {
              const arrayBuffer = reader.result;
              const uint8Array = new Uint8Array(arrayBuffer);
              resolve({
                fileData: Array.from(uint8Array),
                fileName: result.fileName || 'template.xlsx',
                platform: result.platform || '',
                tableData: result.tableData || null, // 返回表格数据
              });
            };
            reader.onerror = () => {
              reject(new Error('读取 Blob 失败'));
            };
            reader.readAsArrayBuffer(result.blob);
          } else {
            console.warn(`文件 ${fileId} 在 IndexedDB 中不存在或数据格式不正确`);
            resolve(null);
          }
        } else {
          console.warn(`文件 ${fileId} 在 IndexedDB 中不存在`);
          resolve(null);
        }
      };
      
      readRequest.onerror = (e) => {
        console.error(`从 IndexedDB 读取文件 ${fileId} 失败:`, e);
        reject(new Error('从 IndexedDB 读取文件失败'));
      };
    });
  } catch (error) {
    console.error(`读取文件 ${fileId} 失败:`, error);
    return null;
  }
};

// 从 IndexedDB 删除文件
export const deleteFileFromIndexedDB = async (fileId) => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(fileId);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        reject(new Error('从 IndexedDB 删除文件失败'));
      };
    });
  } catch (error) {
    console.error('删除文件失败:', error);
    throw error;
  }
};

// 获取所有文件ID列表
export const getAllFileIds = async () => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAllKeys();
      
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      
      request.onerror = () => {
        reject(new Error('获取文件ID列表失败'));
      };
    });
  } catch (error) {
    console.error('获取文件ID列表失败:', error);
    return [];
  }
};

// 获取所有文件信息（包含大小和访问时间）
export const getAllFilesInfo = async () => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onsuccess = () => {
        const files = (request.result || []).map(file => ({
          id: file.id,
          fileName: file.fileName || 'template.xlsx',
          platform: file.platform || '',
          lastAccessedAt: file.lastAccessedAt || file.createdAt || 0,
          createdAt: file.createdAt || 0,
          // 估算大小：fileData数组长度 * 1字节（实际可能更大，但用于排序足够）
          estimatedSize: file.fileData && Array.isArray(file.fileData) 
            ? file.fileData.length 
            : (file.blob ? file.blob.size : 0),
        }));
        resolve(files);
      };
      
      request.onerror = () => {
        reject(new Error('获取文件信息失败'));
      };
    });
  } catch (error) {
    console.error('获取文件信息失败:', error);
    return [];
  }
};

// 清理旧文件缓存（智能清理策略）
export const cleanupOldFiles = async (options = {}) => {
  try {
    const {
      keepFileIds = [], // 需要保留的文件ID列表（如正在使用的）
      maxFiles = 20, // 最多保留的文件数量
      maxAge = 30 * 24 * 60 * 60 * 1000, // 最大保留时间（默认30天，单位：毫秒）
      maxTotalSize = 100 * 1024 * 1024, // 最大总大小（默认100MB，单位：字节）
    } = options;

    const allFiles = await getAllFilesInfo();
    if (allFiles.length === 0) {
      return { deleted: 0, freedSpace: 0 };
    }

    const keepSet = new Set(keepFileIds);
    const now = Date.now();

    // 分类文件：需要保留的 vs 可以删除的
    const filesToKeep = [];
    const filesToDelete = [];

    allFiles.forEach(file => {
      if (keepSet.has(file.id)) {
        // 正在使用的文件，必须保留
        filesToKeep.push(file);
      } else {
        // 检查是否过期
        const age = now - (file.lastAccessedAt || file.createdAt);
        if (age > maxAge) {
          // 超过最大保留时间，标记为可删除
          filesToDelete.push(file);
        } else {
          // 未过期，暂时保留
          filesToKeep.push(file);
        }
      }
    });

    // 如果保留的文件数量超过限制，删除最旧的
    if (filesToKeep.length > maxFiles) {
      // 按最后访问时间排序，最旧的排在前面
      filesToKeep.sort((a, b) => (a.lastAccessedAt || 0) - (b.lastAccessedAt || 0));
      
      // 保留最近使用的 maxFiles 个，其余的移到删除列表
      const toMove = filesToKeep.slice(0, filesToKeep.length - maxFiles);
      filesToDelete.push(...toMove);
      filesToKeep.splice(0, filesToKeep.length - maxFiles);
    }

    // 计算总大小，如果超过限制，删除最大的文件
    let totalSize = filesToKeep.reduce((sum, file) => sum + (file.estimatedSize || 0), 0);
    if (totalSize > maxTotalSize) {
      // 按大小排序，优先删除最大的
      filesToKeep.sort((a, b) => (b.estimatedSize || 0) - (a.estimatedSize || 0));
      
      while (totalSize > maxTotalSize && filesToKeep.length > 0) {
        const file = filesToKeep.shift();
        filesToDelete.push(file);
        totalSize -= (file.estimatedSize || 0);
      }
    }

    // 执行删除（分批删除，避免一次性删除太多）
    const batchSize = 5; // 每批删除5个
    const deleteOne = async (file) => {
      try {
        await deleteFileFromIndexedDB(file.id);
        return { ok: true, size: file.estimatedSize || 0 };
      } catch (error) {
        console.warn(`删除文件 ${file.id} 失败:`, error);
        return { ok: false, size: 0 };
      }
    };

    let deletedCount = 0;
    let freedSpace = 0;
    for (let i = 0; i < filesToDelete.length; i += batchSize) {
      const batch = filesToDelete.slice(i, i + batchSize);
      const results = await Promise.all(batch.map(deleteOne));
      for (const r of results) {
        if (r.ok) {
          deletedCount += 1;
          freedSpace += r.size;
        }
      }
    }

    return {
      deleted: deletedCount,
      freedSpace,
      remaining: filesToKeep.length,
    };
  } catch (error) {
    console.error('清理旧文件失败:', error);
    return { deleted: 0, freedSpace: 0, error: error.message };
  }
};

// 清理 sessionStorage 中的旧数据
export const cleanupSessionStorage = () => {
  try {
    // 清理超过7天的 pendingListingTemplate
    const pendingKey = 'pendingListingTemplate';
    const pendingStr = sessionStorage.getItem(pendingKey);
    
    if (pendingStr) {
      try {
        const pending = JSON.parse(pendingStr);
        // 如果有时间戳且超过7天，则清理
        if (pending.timestamp) {
          const age = Date.now() - pending.timestamp;
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7天
          if (age > maxAge) {
            sessionStorage.removeItem(pendingKey);
          }
        }
      } catch (e) {
        // 解析失败，可能是旧格式，保留
      }
    }
  } catch (error) {
    console.warn('清理 sessionStorage 失败:', error);
  }
};
